import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";
import PageMeta from "../../components/common/PageMeta";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import Button from "../../components/ui/button/Button";
import { Modal } from "../../components/ui/modal";
import { useModal } from "../../hooks/useModal";
import { useToast } from "../../contexts/ToastContext";
import { listManualWBDs, deleteManualWBD } from "../../lib/api/manual-wbd";
import type { ManualWBDRecord } from "../../lib/api/manual-wbd";

const DIAGRAM_TYPE_OPTIONS = [
  { value: "", label: "All Types" },
  { value: "current", label: "Current" },
  { value: "planned", label: "Planned" },
  { value: "as_plugged", label: "As-Plugged" },
];

type DiagramType = ManualWBDRecord["diagram_type"];

function DiagramTypeBadge({ type }: { type: DiagramType }) {
  const config: Record<DiagramType, { label: string; className: string }> = {
    current: {
      label: "Current",
      className:
        "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    },
    planned: {
      label: "Planned",
      className:
        "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    },
    as_plugged: {
      label: "As-Plugged",
      className:
        "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
    },
  };
  const cfg = config[type] ?? config.current;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.className}`}
    >
      {cfg.label}
    </span>
  );
}

function formatRelativeTime(iso: string): string {
  try {
    const now = Date.now();
    const then = new Date(iso).getTime();
    const diffMs = now - then;
    const diffSeconds = Math.floor(diffMs / 1000);
    if (diffSeconds < 60) return "just now";
    const diffMinutes = Math.floor(diffSeconds / 60);
    if (diffMinutes < 60)
      return `${diffMinutes} minute${diffMinutes !== 1 ? "s" : ""} ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24)
      return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 30)
      return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths < 12)
      return `${diffMonths} month${diffMonths !== 1 ? "s" : ""} ago`;
    const diffYears = Math.floor(diffMonths / 12);
    return `${diffYears} year${diffYears !== 1 ? "s" : ""} ago`;
  } catch {
    return iso;
  }
}

export default function ManualWBDList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { error: showError, success: showSuccess } = useToast();

  const [diagrams, setDiagrams] = useState<ManualWBDRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState(
    searchParams.get("diagram_type") ?? ""
  );
  const [api14Filter, setApi14Filter] = useState(
    searchParams.get("api14") ?? ""
  );
  const [api14Input, setApi14Input] = useState(
    searchParams.get("api14") ?? ""
  );
  const [deletingDiagram, setDeletingDiagram] =
    useState<ManualWBDRecord | null>(null);
  const [deleting, setDeleting] = useState(false);
  const deleteModal = useModal();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await listManualWBDs({
          ...(typeFilter ? { diagram_type: typeFilter } : {}),
          ...(api14Filter ? { api14: api14Filter } : {}),
        });
        setDiagrams(data.results);
      } catch (err) {
        showError(
          "Load Failed",
          err instanceof Error ? err.message : "Failed to load diagrams."
        );
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [typeFilter, api14Filter]);

  // Sync filters to URL search params
  useEffect(() => {
    const params: Record<string, string> = {};
    if (typeFilter) params.diagram_type = typeFilter;
    if (api14Filter) params.api14 = api14Filter;
    setSearchParams(params, { replace: true });
  }, [typeFilter, api14Filter, setSearchParams]);

  const handleApi14Search = (e: React.FormEvent) => {
    e.preventDefault();
    setApi14Filter(api14Input.trim());
  };

  const openDeleteModal = (diagram: ManualWBDRecord) => {
    setDeletingDiagram(diagram);
    deleteModal.openModal();
  };

  const handleDelete = async () => {
    if (!deletingDiagram) return;
    setDeleting(true);
    try {
      await deleteManualWBD(deletingDiagram.id);
      setDiagrams((prev) => prev.filter((d) => d.id !== deletingDiagram.id));
      showSuccess("Deleted", "Diagram deleted successfully.");
      deleteModal.closeModal();
    } catch (err) {
      showError(
        "Delete Failed",
        err instanceof Error ? err.message : "Failed to delete diagram."
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <PageMeta
        title="Manual Wellbore Diagrams | RegulAgent"
        description="View and manage saved manual wellbore diagrams"
      />
      <PageBreadcrumb pageTitle="Manual Wellbore Diagrams" />

      <div className="space-y-5">
        {/* Header bar */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white/90">
              Manual Wellbore Diagrams
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Saved wellbore diagrams for your wells
            </p>
          </div>
          <Button onClick={() => navigate("/manual-wbd/new")}>
            Create New Diagram
          </Button>
        </div>

        {/* Filter bar */}
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-sm text-gray-600 dark:text-gray-400 font-medium whitespace-nowrap">
            Filter:
          </label>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="text-sm border border-gray-300 dark:border-white/[0.1] rounded-lg px-3 py-1.5 bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          >
            {DIAGRAM_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <form onSubmit={handleApi14Search} className="flex items-center gap-2">
            <input
              type="text"
              value={api14Input}
              onChange={(e) => setApi14Input(e.target.value)}
              placeholder="Filter by API14..."
              className="text-sm border border-gray-300 dark:border-white/[0.1] rounded-lg px-3 py-1.5 bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500 w-48"
            />
            <button
              type="submit"
              className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 dark:border-white/[0.1] bg-white dark:bg-gray-800 dark:text-white text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Search
            </button>
            {api14Filter && (
              <button
                type="button"
                onClick={() => {
                  setApi14Input("");
                  setApi14Filter("");
                }}
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                Clear
              </button>
            )}
          </form>
        </div>

        {/* Content */}
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-r-transparent" />
                <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                  Loading diagrams...
                </p>
              </div>
            </div>
          ) : diagrams.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <svg
                className="w-12 h-12 text-gray-300 dark:text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"
                />
              </svg>
              <div className="text-center">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  No diagrams found.
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {typeFilter || api14Filter
                    ? "Try adjusting your filters."
                    : "Create one to get started."}
                </p>
              </div>
              {!typeFilter && !api14Filter && (
                <Button onClick={() => navigate("/manual-wbd/new")}>
                  Create New Diagram
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-white/[0.05] bg-gray-50 dark:bg-white/[0.02]">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                      Title
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                      API14
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                      Last Updated
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                  {diagrams.map((diagram) => (
                    <tr
                      key={diagram.id}
                      className="hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors cursor-pointer"
                      onClick={() => navigate(`/manual-wbd/${diagram.id}`)}
                    >
                      <td className="px-4 py-3 font-medium text-gray-800 dark:text-white/90">
                        {diagram.title || (
                          <span className="text-gray-400 dark:text-gray-500 italic">
                            Untitled
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <DiagramTypeBadge type={diagram.diagram_type} />
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400 font-mono text-xs">
                        {diagram.api14}
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {formatRelativeTime(diagram.updated_at)}
                      </td>
                      <td
                        className="px-4 py-3"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() =>
                              navigate(`/manual-wbd/${diagram.id}`)
                            }
                            className="text-sm text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 font-medium transition-colors"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => openDeleteModal(diagram)}
                            className="px-3 py-1.5 rounded-lg bg-red-500 text-white hover:bg-red-600 text-xs font-medium transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.closeModal}
        className="max-w-[500px] p-5 lg:p-10"
      >
        <div className="text-center">
          <div className="relative flex items-center justify-center z-1 mb-7">
            <svg
              className="fill-error-50 dark:fill-error-500/15"
              width="90"
              height="90"
              viewBox="0 0 90 90"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M34.364 6.85053C38.6205 -2.28351 51.3795 -2.28351 55.636 6.85053C58.0129 11.951 63.5594 14.6722 68.9556 13.3853C78.6192 11.0807 86.5743 21.2433 82.2185 30.3287C79.7862 35.402 81.1561 41.5165 85.5082 45.0122C93.3019 51.2725 90.4628 63.9451 80.7747 66.1403C75.3648 67.3661 71.5265 72.2695 71.5572 77.9156C71.6123 88.0265 60.1169 93.6664 52.3918 87.3184C48.0781 83.7737 41.9219 83.7737 37.6082 87.3184C29.8831 93.6664 18.3877 88.0266 18.4428 77.9156C18.4735 72.2695 14.6352 67.3661 9.22531 66.1403C-0.462787 63.9451 -3.30193 51.2725 4.49185 45.0122C8.84391 41.5165 10.2138 35.402 7.78151 30.3287C3.42572 21.2433 11.3808 11.0807 21.0444 13.3853C26.4406 14.6722 31.9871 11.951 34.364 6.85053Z"
                fill=""
                fillOpacity=""
              />
            </svg>
            <span className="absolute -translate-x-1/2 -translate-y-1/2 left-1/2 top-1/2">
              <svg
                className="fill-error-600 dark:fill-error-500"
                width="38"
                height="38"
                viewBox="0 0 38 38"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M9.62684 11.7496C9.04105 11.1638 9.04105 10.2141 9.62684 9.6283C10.2126 9.04252 11.1624 9.04252 11.7482 9.6283L18.9985 16.8786L26.2485 9.62851C26.8343 9.04273 27.7841 9.04273 28.3699 9.62851C28.9556 10.2143 28.9556 11.164 28.3699 11.7498L21.1198 18.9999L28.3699 26.25C28.9556 26.8358 28.9556 27.7855 28.3699 28.3713C27.7841 28.9571 26.8343 28.9571 26.2485 28.3713L18.9985 21.1212L11.7482 28.3715C11.1624 28.9573 10.2126 28.9573 9.62684 28.3715C9.04105 27.7857 9.04105 26.836 9.62684 26.2502L16.8771 18.9999L9.62684 11.7496Z"
                  fill=""
                />
              </svg>
            </span>
          </div>

          <h4 className="mb-2 text-2xl font-semibold text-gray-800 dark:text-white/90 sm:text-title-sm">
            Delete Diagram?
          </h4>
          <p className="text-sm leading-6 text-gray-500 dark:text-gray-400">
            Are you sure you want to delete{" "}
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {deletingDiagram?.title || "this diagram"}
            </span>
            ? This action cannot be undone.
          </p>

          <div className="flex items-center justify-center w-full gap-3 mt-7">
            <Button
              size="sm"
              variant="outline"
              onClick={deleteModal.closeModal}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleDelete}
              disabled={deleting}
              className="bg-error-500 hover:bg-error-600"
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
